import { Component, OnInit } from '@angular/core';
import { BaseDatasourceComponent } from '../base-datasource/base-datasource.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DatasourceConnectionComponent } from '../../datasource-connection/datasource-connection.component';
import { ClientService } from '../../../services/client.service';
import { Message, MessageService, PusherService } from '@rubii/frontend-common';
import { EntraConfig } from '@rubii/share/src/classes/data-source-config';
import { Dsps } from '@rubii/share/src/constants';

@Component({
  selector: 'app-entra',
  templateUrl: './entra.component.html',
  styleUrls: ['./entra.component.scss']
})
export class EntraComponent extends BaseDatasourceComponent implements OnInit {

  currentStep = 1;
  isValidating = false;
  isDomainValid = false;
  domainErrorMessage = '';
  domain = '';

  // Domain prefix pattern: lowercase letters, numbers, hyphens (not at start/end)
  private domainPrefixPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

  constructor(
    private client: ClientService,
    private snackBar: MatSnackBar,
    private message: MessageService<any>,
    private pusher: PusherService,
    private dialogRef: MatDialogRef<DatasourceConnectionComponent>
  ) {
    super();
  }

  ngOnInit(): void {
    this.connectionForm = new FormGroup({
      domainPrefix: new FormControl('', [Validators.required]),
      tenantId: new FormControl('', [Validators.required]),
      clientId: new FormControl('', [Validators.required]),
      clientSecret: new FormControl('', [Validators.required])
    });
    if (window.location.href.toLowerCase().includes('test.rubiiaws.io')) {
      this.domain = 'test.rubiiaws.io';
    } else if (window.location.href.toLowerCase().includes('rubii.io')) {
      this.domain = 'rubii.io';
    } else if (window.location.href.toLowerCase().includes('nova.rubii.dev')) {
      this.domain = 'nova.rubii.dev';
    } else {
      this.domain = 'localhost';
    }
  }

  validateDomainPrefix(): void {
    const domainPrefixControl = this.connectionForm.get('domainPrefix');
    const value = domainPrefixControl.value?.trim().toLowerCase();

    this.isDomainValid = false;

    if (!value) {
      return;
    }

    // Check pattern first
    if (!this.domainPrefixPattern.test(value)) {
      domainPrefixControl.setErrors({ pattern: true });
      return;
    }

    // Clear pattern error if it was set
    if (domainPrefixControl.hasError('pattern')) {
      domainPrefixControl.setErrors(null);
    }

    // Check availability via API - throws error if not available
    this.isValidating = true;
    this.domainErrorMessage = '';

    this.client.account.isDomainAvailable(value)
      .then(() => {
        this.isValidating = false;
        this.isDomainValid = true;
        domainPrefixControl.setErrors(null);
      })
      .catch((e: Error) => {
        this.isValidating = false;
        this.isDomainValid = false;
        this.domainErrorMessage = e.message || 'This domain is not available';
        domainPrefixControl.setErrors({ unavailable: true });
      });
  }

  goToStep1(): void {
    this.currentStep = 1;
  }

  goToStep2(): void {
    this.currentStep = 2;
  }

  goToStep3(): void {
    if (this.isDomainValid) {
      this.currentStep = 3;
    }
  }

  connect(): void {
    // Mark all step 3 fields as dirty for validation
    ['tenantId', 'clientId', 'clientSecret'].forEach(field => {
      this.connectionForm.controls[field].markAsDirty();
      this.connectionForm.controls[field].updateValueAndValidity();
    });

    // Check if step 3 fields are valid
    const step3Valid =
      !this.connectionForm.get('tenantId').errors &&
      !this.connectionForm.get('clientId').errors &&
      !this.connectionForm.get('clientSecret').errors;

    if (step3Valid && this.isDomainValid) {
      const payload = {
        shortname: `${this.connectionForm.get('domainPrefix').value.trim().toLowerCase()}`,
        tenantId: this.connectionForm.get('tenantId').value,
        clientId: this.connectionForm.get('clientId').value,
        clientSecret: this.connectionForm.get('clientSecret').value
      };
      
      this.client.account.saveEntraConfig(payload)
        .catch((e: any) => {
          this.openSnackBarWarning(this.snackBar, e.message || 'Failed to save Entra configuration', 'closed');
          this.message.signalMessage(new Message('data-source-dialog-error-' + Dsps.ENTRA, {payload, domain: this.domain}));
        });
      
        this.dialogRef.close({ dataSourceId: Dsps.ENTRA });
        this.message.signalMessage(new Message('data-source-dialog-closed-' + Dsps.ENTRA, {payload, domain: this.domain}));
    }
  }
}
